import React, { Suspense, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { useGLTF, useTexture } from '@react-three/drei'
import * as THREE from 'three'
import { hasDaxAnimationFeatureFlag } from '../../molecules/URLFeatureFlag/URLFeatureFlag'

// Local copy of the exported Blender model (textures/materials embedded). For
// studio-app/test deploys this is served from the build's /assets path; later
// it can move to the CDN via getModelUrl() (see src/utils/animations.ts).
const ASSET_BASE = `${process.env.PUBLIC_URL ?? ''}/assets/animations/dax`
const MODEL_URL = `${ASSET_BASE}/dax.glb`

// Only pre-fetch the (large) GLB when Dax is actually enabled. This module is
// imported by consumers for its runtime constants (DAX_COMPOSITION_FILL, DAX_CLIP_NAMES)
// even off-flag, so guard the download so non-flagged accounts never fetch the asset.
if (hasDaxAnimationFeatureFlag()) useGLTF.preload(MODEL_URL)

// Camera + fit constants. The model is scaled so its height is TARGET_HEIGHT world units, viewed by a
// camera whose visible frustum at the model is FRUSTUM_HEIGHT units tall (2 * distance * tan(fov/2)).
// The drawn composition therefore covers a known fraction of the canvas; callers size their box with the
// DAX_*_FILL exports below so the ART (not the box) hits a target height. CAMERA_Z is derived further
// down, once the art's full height is known, so the frustum always has room for it.
const CAMERA_FOV = 40
const TARGET_HEIGHT = 2.7
const HALF_FOV_TAN = Math.tan((CAMERA_FOV / 2) * (Math.PI / 180))

// The saucer Dax stands on is 2D art (the shading/perspective is baked in), so it's drawn as a flat,
// camera-facing billboard rather than 3D geometry. The camera looks head-on (down the -Z axis), so a
// plane in the default XY orientation already faces it. Served from /public.
const SAUCER_URL = `${process.env.PUBLIC_URL ?? ''}/assets/images/Saucer.svg`
// Feet sit at y = -TARGET_HEIGHT / 2 (Dax is re-centered on the composition group's origin). Exposed as
// a tunable in case the model's feet don't land exactly at the mid-height.
const PLATFORM_TOP_Y = -TARGET_HEIGHT / 2
// Saucer art is 198x101 (~1.96:1). Width in world units, height derived from that aspect.
const SAUCER_ART_WIDTH_PX = 198
const SAUCER_ASPECT = SAUCER_ART_WIDTH_PX / 101
const SAUCER_WIDTH = TARGET_HEIGHT * 0.8
const SAUCER_HEIGHT = SAUCER_WIDTH / SAUCER_ASPECT
// World units per pixel of the design art. Other pieces of the composition are sized through this so
// they render at the size they were exported at, relative to the saucer, instead of being scaled on
// their own.
const ART_PIXEL = SAUCER_WIDTH / SAUCER_ART_WIDTH_PX
// Where Dax's feet sit on the art, as a fraction from its TOP edge. This is what decides where on the
// disc he stands: low values put him on the back rim, higher values walk him forward into the middle
// of the top face. Raising it slides the art UP relative to his feet, which eats into the visible disc,
// so it is kept low and he stands on the border.
const SAUCER_FOOT_ANCHOR = 0.34
// Push the saucer slightly back so Dax reads as standing in front of it. There is no sideways nudge: the
// saucer centers on the tracked lower-body bone average so Dax stands in the middle of the disc.
const PLATFORM_Z_BACK = TARGET_HEIGHT * 0.14
// How far the saucer's front rim hangs below Dax's foot line.
const SAUCER_BELOW_FEET = (1 - SAUCER_FOOT_ANCHOR) * SAUCER_HEIGHT

// The glow beneath the saucer: four blurred rings that taper downward, drawn as a second billboard that
// hangs from the disc's front rim. Filename is URL-encoded because the design export has a space in it.
const SAUCER_GLOW_URL = `${process.env.PUBLIC_URL ?? ''}/assets/images/Saucer%20glow.svg`
// Glow art is 84x41, drawn at the same scale as the saucer's art rather than stretched to the disc's
// width, so it stays the beam it was designed as (a bit under half the disc wide).
const SAUCER_GLOW_WIDTH = 84 * ART_PIXEL
const SAUCER_GLOW_HEIGHT = 41 * ART_PIXEL
// How much of the beam's top hides behind the disc, as a fraction of the glow's height. Set to the
// center of its widest ring (which spans y 1-23 of 41), so that ring's top arc goes behind the disc and
// the beam blends into it instead of starting as a detached ellipse. Raising it tucks more away.
const SAUCER_GLOW_RIM_OVERLAP = 12 / 41
// Keeps the glow's plane behind the saucer's, so the disc wins wherever the two overlap. Small enough
// that the billboards stay visually coplanar.
const GLOW_Z_BACK = 0.01
// Thruster flicker. The beam is the saucer's propulsion, so it never sits still: it stretches and dims on
// a rhythm built from two out-of-step sines, which reads as an engine rather than a loop. Amounts are
// fractions of the beam's resting length and of full opacity.
const GLOW_FLICKER_LENGTH = 0.12
const GLOW_FLICKER_OPACITY = 0.15
const GLOW_REST_OPACITY = 0.85
// The flicker stretches the beam DOWNWARD from where it meets the disc, so the art reaches its longest
// at the top of the cycle; the layout below reserves room for that so a flare can never be clipped.
const GLOW_BELOW_SAUCER = (1 - SAUCER_GLOW_RIM_OVERLAP) * SAUCER_GLOW_HEIGHT * (1 + GLOW_FLICKER_LENGTH)
// Trailing plume. Exhaust doesn't move rigidly with the craft, so instead of riding along with the disc
// each row of the beam chases the saucer's position on its own time constant (seconds; bigger lags more):
// the row that meets the disc keeps up almost exactly, the smallest ring drags well behind, and the rows
// between bend the plume into a curve. The plane is given height segments so the art can shear smoothly
// with no seam, and the gain exaggerates the offsets, which are otherwise sub-pixel at this size.
const GLOW_TRAIL_SEGMENTS = 12
const GLOW_TRAIL_TAU_TOP = 0.05
const GLOW_TRAIL_TAU_BOTTOM = 0.45
const GLOW_TRAIL_GAIN = 3
// Walks Dax forward onto the middle of the disc (world units): larger moves him toward the front rim,
// smaller sits him further back. SAUCER_FOOT_ANCHOR would achieve the same relative shift, but it does
// so by sliding the ART up behind him, which shrinks the disc on screen. Lowering Dax instead leaves
// the saucer's size and screen position untouched, so only he moves.
const DAX_STAND_FORWARD = 0.06
// Makes Dax taller without touching the saucer or the bubble. He is re-centered on his own bounds every
// frame, so scaling him up grows him evenly in both directions and his feet would sink below the
// saucer's standing surface; lifting him by half the added height pins the foot line back where it was.
const DAX_HEIGHT_SCALE = 1.1
const DAX_GROWTH_FOOT_FIX = ((DAX_HEIGHT_SCALE - 1) * TARGET_HEIGHT) / 2

// Where the drawn art starts and ends, measured in the composition group's own space (i.e. before
// DAX_LIFT is applied). Dax is re-centered on his own bounds each frame, so his body straddles the group
// origin; the saucer hangs below his nominal foot line and the glow below the saucer's rim. All derived
// from the tuning constants above so they can never drift out of sync.
const DAX_BODY_HEIGHT = TARGET_HEIGHT * DAX_HEIGHT_SCALE
const ART_TOP_LOCAL_Y = -DAX_STAND_FORWARD + DAX_GROWTH_FOOT_FIX + DAX_BODY_HEIGHT / 2
const ART_BOTTOM_LOCAL_Y = PLATFORM_TOP_Y - SAUCER_BELOW_FEET - GLOW_BELOW_SAUCER
const ART_HEIGHT = ART_TOP_LOCAL_Y - ART_BOTTOM_LOCAL_Y

// Pull the camera back until the frustum holds the whole art, plus a hair at each edge so antialiasing
// doesn't nibble the crown or the glow's last ring, then lift the art to sit centered in it. Deriving the
// distance (rather than fixing it) means adding to the art below the saucer can never clip it.
const EDGE_MARGIN = 0.02
const CAMERA_Z = (ART_HEIGHT + 2 * EDGE_MARGIN) / (2 * HALF_FOV_TAN)
const FRUSTUM_HEIGHT = 2 * CAMERA_Z * HALF_FOV_TAN
const DAX_LIFT = -(ART_TOP_LOCAL_Y + ART_BOTTOM_LOCAL_Y) / 2

const DAX_CENTER_Y = DAX_LIFT - DAX_STAND_FORWARD + DAX_GROWTH_FOOT_FIX
const DAX_HEAD_TOP_Y = DAX_CENTER_Y + DAX_BODY_HEIGHT / 2
const ART_BOTTOM_Y = ART_BOTTOM_LOCAL_Y + DAX_LIFT
/** Fraction of the canvas height from the TOP of the canvas down to the crown of Dax's head. */
export const DAX_HEAD_TOP_FILL = (FRUSTUM_HEIGHT / 2 - DAX_HEAD_TOP_Y) / FRUSTUM_HEIGHT
/** Fraction of the canvas height from the BOTTOM of the canvas up to the bottom of the drawn art (the
 *  glow's last ring below the saucer). */
export const DAX_ART_BOTTOM_FILL = (ART_BOTTOM_Y + FRUSTUM_HEIGHT / 2) / FRUSTUM_HEIGHT
/** Fraction of the canvas height covered by the drawn art (head crown down to the glow's last ring).
 *  Callers that need the VISIBLE art to hit a target height divide that target by this. */
export const DAX_COMPOSITION_FILL = (DAX_HEAD_TOP_Y - ART_BOTTOM_Y) / FRUSTUM_HEIGHT

/** The saucer Dax stands on, and the glow beneath it: the Saucer.svg / Saucer glow.svg art on flat,
 *  camera-facing, unlit billboards (so their colors show exactly as designed). The glow flickers like a
 *  thruster and trails behind the saucer as it drifts. Forwards a ref to the group so callers can track
 *  it to his feet. */
const DaxPlatform = React.forwardRef<THREE.Group, { reduceMotion?: boolean }>(({ reduceMotion = false }, ref) => {
  const [saucer, glow] = useTexture([SAUCER_URL, SAUCER_GLOW_URL])
  // The caller drives this group's position to keep the disc under Dax's feet, so the beam reads it to
  // know what it is trailing. Held locally and handed out, rather than only forwarded, so the animation
  // below doesn't have to reach for it through the scene graph.
  const platform = useRef<THREE.Group>(null)
  useImperativeHandle(ref, () => platform.current as THREE.Group)
  // Line the art's standing surface up with the foot line: the anchor point (SAUCER_FOOT_ANCHOR from the
  // top) sits at PLATFORM_TOP_Y, so the plane's center is offset below it by the remaining half-height.
  const centerY = PLATFORM_TOP_Y - (0.5 - SAUCER_FOOT_ANCHOR) * SAUCER_HEIGHT
  // Hang the glow from the saucer's front rim (the bottom edge of the disc's art), centered under it and
  // raised by SAUCER_GLOW_RIM_OVERLAP so its top disappears behind the disc. The beam is wrapped in a
  // group pinned to that meeting point and offset down by its own half-height, so scaling the group's Y
  // lengthens the beam away from the disc instead of growing it out of both ends.
  const rimY = PLATFORM_TOP_Y - SAUCER_BELOW_FEET
  const beamTopY = rimY + SAUCER_GLOW_RIM_OVERLAP * SAUCER_GLOW_HEIGHT

  const beam = useRef<THREE.Group>(null)
  const beamMaterial = useRef<THREE.MeshBasicMaterial>(null)
  const beamGeometry = useRef<THREE.PlaneGeometry>(null)
  // Per-vertex trail state, built from the plane's own layout on the first frame: where the vertex
  // started, how far up the beam it sits (0 at the smallest ring, 1 where it meets the disc), and the
  // saucer position it is currently chasing.
  const trail = useRef<{ baseX: Float32Array, height: Float32Array, chasing: Float32Array } | null>(null)

  useFrame((state, delta) => {
    const grp = beam.current
    const mat = beamMaterial.current
    const geometry = beamGeometry.current
    const disc = platform.current
    if (grp === null || mat === null || geometry === null || disc === null) return

    // Two sines at unrelated speeds, so the engine note never settles into an obvious repeat.
    const t = state.clock.getElapsedTime()
    const pulse = reduceMotion ? 0 : Math.sin(t * 9) * 0.6 + Math.sin(t * 14.7 + 1.3) * 0.4
    grp.scale.y = 1 + GLOW_FLICKER_LENGTH * pulse
    mat.opacity = GLOW_REST_OPACITY + GLOW_FLICKER_OPACITY * pulse

    const vertices = geometry.attributes.position
    const x = disc.position.x
    if (trail.current === null) {
      const baseX = new Float32Array(vertices.count)
      const height = new Float32Array(vertices.count)
      const chasing = new Float32Array(vertices.count)
      for (let i = 0; i < vertices.count; i += 1) {
        baseX[i] = vertices.getX(i)
        height[i] = vertices.getY(i) / SAUCER_GLOW_HEIGHT + 0.5
        chasing[i] = x
      }
      trail.current = { baseX, height, chasing }
    }

    const { baseX, height, chasing } = trail.current
    for (let i = 0; i < vertices.count; i += 1) {
      const tau = GLOW_TRAIL_TAU_BOTTOM + (GLOW_TRAIL_TAU_TOP - GLOW_TRAIL_TAU_BOTTOM) * height[i]
      // Exponential chase, framed in seconds so the lag is the same however fast the display refreshes.
      chasing[i] = reduceMotion ? x : chasing[i] + (x - chasing[i]) * (1 - Math.exp(-delta / tau))
      vertices.setX(i, baseX[i] + (chasing[i] - x) * GLOW_TRAIL_GAIN)
    }
    vertices.needsUpdate = true
  })

  return (
    <group ref={platform}>
      <group ref={beam} position={[0, beamTopY, -GLOW_Z_BACK]}>
        <mesh position={[0, -SAUCER_GLOW_HEIGHT / 2, 0]}>
          <planeGeometry ref={beamGeometry} args={[SAUCER_GLOW_WIDTH, SAUCER_GLOW_HEIGHT, 1, GLOW_TRAIL_SEGMENTS]} />
          <meshBasicMaterial
            ref={beamMaterial}
            map={glow}
            transparent
            opacity={GLOW_REST_OPACITY}
            depthWrite={false}
            toneMapped={false}
          />
        </mesh>
      </group>
      <mesh position={[0, centerY, 0]}>
        <planeGeometry args={[SAUCER_WIDTH, SAUCER_HEIGHT]} />
        <meshBasicMaterial map={saucer} transparent depthWrite={false} toneMapped={false} />
      </mesh>
    </group>
  )
})
DaxPlatform.displayName = 'DaxPlatform'

export type DaxReaction = 'idle' | 'correct' | 'incorrect'

// Unique character animation clips in the export. Excludes the plane/path/util
// clips ("Plane.002Action.001", "BezierCircleAction", "KeyAction.001") and the
// exact duplicates ("Excited.001" == "Excited"; "Walking2"/"Walking2.001" ==
// "WalkCycle"). "Waving.001" is kept because it differs from "Waving".
export const DAX_CLIP_NAMES = [
  'Action', 'Excited', 'Nod', 'WalkCycle', 'WaveGesture', 'Waving', 'Waving.001'
]

// Friendly labels for the preview picker (the keys are the real clip names used
// to look the animation up). "Action" is the full sequence of every move.
export const DAX_CLIP_LABELS: Record<string, string> = {
  Action: 'All in one'
}

interface DaxAnimationProps {
  /** Bumps whenever we want Dax to react (e.g. an answer was submitted). */
  reactionKey?: number
  /** Which reaction to play. */
  reaction?: DaxReaction
  /** Force a specific animation clip by name (used by the preview picker). */
  overrideClip?: string
  /** Skip motion for users who prefer reduced motion. */
  reduceMotion?: boolean
  /** Render the golden pedestal disc under Dax's feet, and its glow. */
  showPlatform?: boolean
  /** Called once Dax has been drawn, so callers can reveal the rest of the composition with him. */
  onReady?: () => void
  className?: string
  /** Prefer explicit pixel width/height so the Canvas's first measure is not 300x150 (DMVP-1094). */
  style?: React.CSSProperties
  /** Explicit CSS-pixel width. When set with height, WebGL size is forced (skips DOM measure). */
  width?: number
  /** Explicit CSS-pixel height. When set with width, WebGL size is forced (skips DOM measure). */
  height?: number
}

/**
 * Applies a short procedural "reaction" burst (hop + spin for correct, shake for
 * incorrect) on top of whatever baked animation is playing. Optionally adds a
 * gentle idle bob (used by the placeholder, which has no baked idle clip).
 */
const useReactionMotion = (
  group: React.RefObject<THREE.Group>,
  reaction: DaxReaction,
  reactionKey: number,
  reduceMotion: boolean,
  idleBob: boolean
): void => {
  const burstRef = useRef<number>(0)

  // Re-fire on every reactionKey bump, even if the reaction type is unchanged.
  useEffect(() => {
    if (reaction !== 'idle') burstRef.current = 1
  }, [reaction, reactionKey])

  useFrame((state, delta) => {
    const grp = group.current
    if (grp === null) return

    if (reduceMotion) {
      grp.position.y = 0
      grp.rotation.set(0, 0, 0)
      grp.scale.setScalar(1)
      return
    }

    const t = state.clock.getElapsedTime()
    grp.position.y = idleBob ? Math.sin(t * 2) * 0.08 : 0
    grp.rotation.set(0, idleBob ? Math.sin(t * 0.8) * 0.25 : 0, 0)

    if (burstRef.current > 0) {
      burstRef.current = Math.max(0, burstRef.current - delta)
      const p = burstRef.current // 1 -> 0
      if (reaction === 'correct') {
        grp.position.y += Math.sin((1 - p) * Math.PI) * 0.6
        grp.rotation.y += (1 - p) * Math.PI * 2
        grp.scale.setScalar(1 + Math.sin((1 - p) * Math.PI) * 0.15)
      } else if (reaction === 'incorrect') {
        grp.rotation.z = Math.sin((1 - p) * Math.PI * 8) * 0.2
      }
    } else {
      grp.scale.setScalar(1)
    }
  })
}

// Clip preferences. The export names its clips descriptively; the plane/util
// clips ("Plane.002Action.001", "BezierCircleAction", "KeyAction.001", "Action")
// are ignored.
const IDLE_CLIPS = ['Waving', 'WaveGesture', 'Nod', 'WalkCycle']

/** The real exported Dax model. */
const DaxModel: React.FC<{ reaction: DaxReaction, reactionKey: number, reduceMotion: boolean, overrideClip?: string, showPlatform?: boolean }> = ({ reaction, reactionKey, reduceMotion, overrideClip, showPlatform = false }) => {
  const group = useRef<THREE.Group>(null)
  const { scene, animations } = useGLTF(MODEL_URL)
  const names = useMemo(() => animations.map((c) => c.name), [animations])
  // Drive the clips with our own mixer (rather than drei's useAnimations) so
  // playback is deterministic and doesn't depend on when drei populates its
  // action map — which was leaving Dax frozen in the bind-pose T-pose.
  const mixer = useMemo(() => new THREE.AnimationMixer(scene), [scene])
  useFrame((_, delta) => mixer.update(delta))

  // Normalize export artifacts in the embedded materials: Skin/Pants/Sweater
  // ship with metallicFactor unset (glTF defaults to 1.0), so they render as
  // dark metal without an environment map; and Pants/Sweater carry a stray gray
  // emissive that washes their color. Force the character matte and drop the
  // emissive so the baked textures show their true (bright) colors.
  useEffect(() => {
    scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh
      if (!mesh.isMesh || /plane|floor/i.test(obj.name)) return
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
      mats.forEach((mat) => {
        const m = mat as THREE.MeshStandardMaterial
        m.metalness = 0
        m.emissive?.setRGB(0, 0, 0)
        m.needsUpdate = true
      })
    })
  }, [scene])

  // Remove the exported ground plane, then center + scale using the UNION of all
  // character sub-meshes. (GLTFLoader splits "Blippy" into one mesh per material,
  // so we can't rely on a single mesh. We also skip the plane/floor and any
  // helper geometry, and use each mesh's bind-pose geometry box, which is
  // reliable for skinned meshes.)
  const fitScale = useMemo(() => {
    scene.updateWorldMatrix(true, true)

    const toRemove: THREE.Object3D[] = []
    const box = new THREE.Box3()
    scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh
      if (!mesh.isMesh) return
      if (/plane|floor/i.test(obj.name)) {
        toRemove.push(obj)
        return
      }
      if (mesh.geometry.boundingBox === null) mesh.geometry.computeBoundingBox()
      const meshBox = mesh.geometry.boundingBox
      if (meshBox !== null) {
        box.union(meshBox.clone().applyMatrix4(mesh.matrixWorld))
      }
    })
    toRemove.forEach((obj) => obj.removeFromParent())

    if (box.isEmpty()) return 1
    const size = box.getSize(new THREE.Vector3())
    // Fit by HEIGHT (not the max dimension) so the arm span in a wave/idle pose
    // doesn't drive the scale, then apply DAX_HEIGHT_SCALE on top of the nominal
    // TARGET_HEIGHT, leaving margin for the live re-centering below.
    const height = size.y || 1
    return (TARGET_HEIGHT * DAX_HEIGHT_SCALE) / height
  }, [scene])

  // Live skeleton for per-frame re-centering. The animated pose sits differently
  // than the static bind pose, so centering on the bind box alone left Dax
  // dropping out of frame; instead we recenter on the actual bones each frame.
  const bones = useMemo(() => {
    let found: THREE.Bone[] = []
    scene.traverse((obj) => {
      const sk = obj as THREE.SkinnedMesh
      if (sk.isSkinnedMesh && found.length === 0) found = sk.skeleton.bones
    })
    return found
  }, [scene])

  const fitRef = useRef<THREE.Group>(null)
  const platformRef = useRef<THREE.Group>(null)
  const boneBox = useMemo(() => new THREE.Box3(), [])
  const tmp = useMemo(() => new THREE.Vector3(), [])
  const tmp2 = useMemo(() => new THREE.Vector3(), [])
  useFrame(() => {
    const fit = fitRef.current
    const grp = group.current
    if (fit === null || grp === null || bones.length === 0) return
    boneBox.makeEmpty()
    for (const bone of bones) {
      bone.getWorldPosition(tmp)
      boneBox.expandByPoint(tmp)
    }
    boneBox.getCenter(tmp)
    grp.worldToLocal(tmp) // model center expressed in the (reaction) group's local space
    // One-step correction that keeps the model centered at the group origin,
    // independent of the reaction hop/spin applied to `grp`.
    fit.position.x -= tmp.x
    fit.position.y -= tmp.y
    fit.position.z -= tmp.z

    // Keep the pedestal centered under Dax's FEET, not the full-body bounds center: an extended
    // arm/gesture skews the overall center, so average only the lower-body bones (bottom quarter
    // of the skeleton's height) to find where he's actually standing.
    const platform = platformRef.current
    if (platform !== null) {
      const cutoff = boneBox.min.y + (boneBox.max.y - boneBox.min.y) * 0.25
      let fx = 0; let fz = 0; let fn = 0
      for (const bone of bones) {
        bone.getWorldPosition(tmp2)
        if (tmp2.y <= cutoff) { fx += tmp2.x; fz += tmp2.z; fn += 1 }
      }
      if (fn > 0) {
        platform.position.x = fx / fn
        platform.position.z = fz / fn - PLATFORM_Z_BACK
      }
    }
  })

  // The idle clip to loop (or the override from the preview picker). The plane/
  // path/util clips are ignored so Dax never stands in the plane's T-pose.
  const desiredClip = useMemo(() => {
    if (overrideClip !== undefined && names.includes(overrideClip)) return overrideClip
    return IDLE_CLIPS.find((name) => names.includes(name)) ??
      names.find((n) => !/plane|bezier|key/i.test(n) && n !== 'Action') ?? null
  }, [names, overrideClip])

  // Base idle/override loop. Held in a ref so the reaction can fade it out/in.
  const idleActionRef = useRef<THREE.AnimationAction | null>(null)
  useEffect(() => {
    if (desiredClip === null) return
    const clip = animations.find((c) => c.name === desiredClip)
    if (clip === undefined) return
    const action = mixer.clipAction(clip)
    idleActionRef.current = action
    action.reset().setLoop(THREE.LoopRepeat, Infinity).fadeIn(0.3).play()
    return () => {
      action.fadeOut(0.2)
      action.stop()
      if (idleActionRef.current === action) idleActionRef.current = null
    }
  }, [mixer, animations, desiredClip])

  // On a correct answer (reactionKey bumps with reaction === 'correct'), play the
  // one-shot "Excited" clip, then fade back to the idle loop when it finishes.
  useEffect(() => {
    if (reaction !== 'correct' || reduceMotion || reactionKey === 0) return
    const clip = animations.find((c) => c.name === 'Excited')
    if (clip === undefined) return
    const idle = idleActionRef.current
    const excited = mixer.clipAction(clip)
    excited.reset().setLoop(THREE.LoopOnce, 1)
    excited.clampWhenFinished = false
    idle?.fadeOut(0.2)
    excited.fadeIn(0.2).play()

    // Return to idle after the clip's duration (LoopOnce doesn't loop back on its own).
    const timer = setTimeout(() => {
      excited.fadeOut(0.3)
      idle?.reset().fadeIn(0.3).play()
    }, clip.duration * 1000)

    return () => clearTimeout(timer)
  }, [mixer, animations, reaction, reactionKey, reduceMotion])

  return (
    <group position={[0, DAX_LIFT, 0]}>
      {/* Only Dax is lowered; the saucer keeps its position so its on-screen size never changes. */}
      <group position={[0, -DAX_STAND_FORWARD + DAX_GROWTH_FOOT_FIX, 0]}>
        <group ref={group}>
          <group ref={fitRef} scale={fitScale}>
            <primitive object={scene} />
          </group>
        </group>
      </group>
      {showPlatform && <DaxPlatform ref={platformRef} reduceMotion={reduceMotion} />}
    </group>
  )
}

/**
 * Placeholder character (primitives) used if the GLB fails to load, so the
 * overlay never renders empty.
 */
const PlaceholderDax: React.FC<{ reaction: DaxReaction, reactionKey: number, reduceMotion: boolean, showPlatform?: boolean }> = ({ reaction, reactionKey, reduceMotion, showPlatform = false }) => {
  const group = useRef<THREE.Group>(null)
  useReactionMotion(group, reaction, reactionKey, reduceMotion, true)

  return (
    <group position={[0, DAX_LIFT, 0]}>
      {/* Only Dax is lowered; the saucer keeps its position so its on-screen size never changes. */}
      <group position={[0, -DAX_STAND_FORWARD + DAX_GROWTH_FOOT_FIX, 0]} scale={DAX_HEIGHT_SCALE}>
        <group ref={group}>
          <mesh position={[0, 0.1, 0]}>
            <capsuleGeometry args={[0.5, 0.7, 8, 16]} />
            <meshStandardMaterial color='#9FE1CB' />
          </mesh>
          <mesh position={[0, 1.05, 0]}>
            <sphereGeometry args={[0.45, 32, 32]} />
            <meshStandardMaterial color='#C0DD97' />
          </mesh>
          <mesh position={[0.18, 1.12, 0.38]}>
            <sphereGeometry args={[0.08, 16, 16]} />
            <meshStandardMaterial color='#3E4140' />
          </mesh>
          <mesh position={[-0.18, 1.12, 0.38]}>
            <sphereGeometry args={[0.08, 16, 16]} />
            <meshStandardMaterial color='#3E4140' />
          </mesh>
        </group>
      </group>
      {showPlatform && <DaxPlatform reduceMotion={reduceMotion} />}
    </group>
  )
}

/**
 * Reports that the scene has drawn a frame with Dax in it. It sits INSIDE the Suspense boundary, so
 * it only mounts once the GLB has resolved (or the error boundary has swapped in the placeholder),
 * and it waits one further frame so the signal lands after Dax has actually been painted. Callers
 * use this to reveal the rest of their composition at the same moment as Dax, instead of showing
 * the instant HTML parts - the speech bubble - while the canvas is still coming up.
 */
const DaxReadySignal: React.FC<{ onReady: () => void }> = ({ onReady }) => {
  const framesDrawn = useRef(0)

  useFrame(() => {
    if (framesDrawn.current > 1) return
    framesDrawn.current += 1
    if (framesDrawn.current > 1) onReady()
  })

  return null
}

/**
 * Forces the R3F drawing buffer to an explicit pixel size. DOM measure is what left Dax stuck at
 * the HTML canvas default (~300x150) on first show (DMVP-1094); when the caller already knows the
 * box size, push that into the renderer instead of trusting ResizeObserver.
 */
const ForceCanvasSize: React.FC<{ width: number, height: number }> = ({ width, height }) => {
  const setSize = useThree((state) => state.setSize)

  useLayoutEffect(() => {
    if (width > 0 && height > 0) setSize(width, height)
  }, [width, height, setSize])

  // Read size from the live frame state (not a React snapshot). ResizeObserver can overwrite a good
  // setSize on the next frame; keep pinning until the store matches (DMVP-1094). Opening DevTools
  // also fixed him via resize — same underlying "measure caught up late" failure.
  useFrame((state) => {
    if (width <= 0 || height <= 0) return
    if (Math.abs(state.size.width - width) > 0.5 || Math.abs(state.size.height - height) > 0.5) {
      state.setSize(width, height)
    }
  })

  return null
}

interface FallbackBoundaryProps {
  fallback: React.ReactNode
  children: React.ReactNode
}

/** Falls back to the placeholder if the GLB can't be loaded. */
class DaxErrorBoundary extends React.Component<FallbackBoundaryProps, { hasError: boolean }> {
  constructor (props: FallbackBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError (): { hasError: boolean } {
    return { hasError: true }
  }

  render (): React.ReactNode {
    return this.state.hasError ? this.props.fallback : this.props.children
  }
}

/**
 * Absolutely-positioned, non-interactive 3D overlay that renders Dax on top of
 * the standard (graphic/Unity) MCQ view. Gated by the caller via
 * `hasDaxAnimationFeatureFlag()`.
 */
const DaxAnimation: React.FC<DaxAnimationProps> = ({
  reactionKey = 0,
  reaction = 'idle',
  overrideClip,
  reduceMotion = false,
  showPlatform = false,
  onReady,
  className = '',
  style,
  width,
  height
}: DaxAnimationProps) => {
  const hasExplicitSize = width !== undefined && height !== undefined && width > 0 && height > 0
  const boxStyle: React.CSSProperties = {
    ...style,
    ...(hasExplicitSize ? { width, height } : {})
  }

  return (
    <div
      data-testid='dax-animation'
      aria-hidden='true'
      className={`pointer-events-none ${className}`}
      style={boxStyle}
    >
      {/*
        When width/height props are set, force that size into WebGL (DMVP-1094). Relying on
        ResizeObserver alone left him stuck at the HTML default ~300x150 on first show.
        Do NOT key the Canvas on width/height — that remounts on every resize tick and makes
        him vanish while the window is being dragged.
      */}
      <Canvas
        camera={{ position: [0, 0, CAMERA_Z], fov: CAMERA_FOV }}
        gl={{ alpha: true }}
        resize={hasExplicitSize ? undefined : { debounce: 0, scroll: true }}
        style={{
          width: hasExplicitSize ? width : '100%',
          height: hasExplicitSize ? height : '100%',
          background: 'transparent'
        }}
        onCreated={hasExplicitSize
          ? ({ setSize }) => { setSize(width as number, height as number) }
          : undefined}
      >
        {hasExplicitSize && <ForceCanvasSize width={width as number} height={height as number} />}
        <ambientLight intensity={0.7} />
        <hemisphereLight args={['#ffffff', '#8d8d8d', 1.0]} />
        <directionalLight position={[3, 5, 2]} intensity={1.6} />
        <directionalLight position={[-3, 2, -2]} intensity={0.5} />
        <Suspense fallback={null}>
          <DaxErrorBoundary
            fallback={<PlaceholderDax reaction={reaction} reactionKey={reactionKey} reduceMotion={reduceMotion} showPlatform={showPlatform} />}
          >
            <DaxModel reaction={reaction} reactionKey={reactionKey} reduceMotion={reduceMotion} overrideClip={overrideClip} showPlatform={showPlatform} />
          </DaxErrorBoundary>
          {onReady !== undefined && <DaxReadySignal onReady={onReady} />}
        </Suspense>
      </Canvas>
    </div>
  )
}

export default DaxAnimation
